import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"
import type { IconName } from "../Icon/types"

const badgeVariants = cva(
  "inline-flex items-center justify-center font-500 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-0 rounded-100",
  {
    variants: {
      variant: {
        info: "bg-feedback-info-background text-feedback-base-text",
        success: "bg-feedback-success-background text-feedback-base-text",
        danger: "bg-feedback-danger-background text-feedback-base-text",
        secondary: "bg-feedback-secondary-background text-feedback-base-text",
        tertiary: "bg-action-tertiary-background-default text-feedback-base-text",
      },
      size: {
        xs: "text-root-size-body-md py-0 gap-p5 leading-1",
        sm: "text-root-size-body-lg py-1 gap-1 leading-1",
        md: "text-root-size-body-xl py-2 gap-1 leading-1",
      },
    },
    defaultVariants: {
      variant: "info",
      size: "md",
    },
  }
)

type BadgeSize = VariantProps<typeof badgeVariants>["size"]

/**
 * Generates appropriate padding classes based on icon presence and badge size
 */
const getBadgePadding = (hasIcon: boolean, size: BadgeSize): string => {
  if (hasIcon) {
    switch (size) {
      case "xs":
        return "pl-1 pr-2"
      case "sm":
        return "pl-2 pr-4"
      default:
        return "pl-3 pr-4"
    }
  }

  switch (size) {
    case "xs":
      return "px-2"
    default:
      return "px-4"
  }
}

const BadgeIcon = ({ icon }: { icon: IconName }) => {
  return <div className="shrink-0">{<Icon name={icon} />}</div>
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional icon to display before the text */
  icon?: IconName
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    const paddingClasses = getBadgePadding(!!icon, size)

    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size }), paddingClasses, className)}
        {...props}
      >
        {icon && <BadgeIcon icon={icon} />}
        {children}
      </div>
    )
  }
)
Badge.displayName = "Badge"

export interface ActionBadgeProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    keyof VariantProps<typeof badgeVariants>
  > {
  /** Visual style variant of the badge */
  variant?: VariantProps<typeof badgeVariants>["variant"]
  /** Size of the badge affecting text and padding */
  size?: VariantProps<typeof badgeVariants>["size"]
  /** Optional icon to display before the text */
  icon?: IconName
}

const ActionBadge = React.forwardRef<HTMLButtonElement, ActionBadgeProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    const paddingClasses = getBadgePadding(!!icon, size)

    return (
      <button
        ref={ref}
        className={cn(
          badgeVariants({ variant, size }),
          paddingClasses,
          "cursor-pointer",
          "focus:ring-0 focus:outline-none",
          className
        )}
        {...props}
      >
        {icon && <BadgeIcon icon={icon} />}
        {children}
      </button>
    )
  }
)
ActionBadge.displayName = "ActionBadge"

export { Badge, ActionBadge, badgeVariants }
